import { useState, useEffect } from 'react';
import {
  isMaintenanceModeEnabled,
  enableMaintenanceMode,
  disableMaintenanceMode,
} from '../../utils/maintenanceMode';
import {
  isUpgradePopupEnabled,
  setUpgradePopupEnabled,
} from '../../utils/upgradePopupMode';

export default function OverridePage() {
  const [maintenanceOn, setMaintenanceOn] = useState(false);
  const [upgradeOn, setUpgradeOn] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    document.title = 'Site Override Controls';
    setMaintenanceOn(isMaintenanceModeEnabled());
    setUpgradeOn(isUpgradePopupEnabled());
  }, []);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const handleToggleMaintenance = () => {
    if (maintenanceOn) {
      disableMaintenanceMode();
      setMaintenanceOn(false);
      showToast('Maintenance mode turned OFF');
    } else {
      enableMaintenanceMode();
      setMaintenanceOn(true);
      showToast('Maintenance mode turned ON');
    }
  };

  const handleToggleUpgrade = () => {
    const next = !upgradeOn;
    setUpgradePopupEnabled(next);
    setUpgradeOn(next);
    showToast(`Upgrade popup ${next ? 'enabled' : 'disabled'}`);
  };

  const handleDisableAll = () => {
    disableMaintenanceMode();
    setUpgradePopupEnabled(false);
    setMaintenanceOn(false);
    setUpgradeOn(false);
    showToast('All overrides turned OFF — site is live!');
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      {/* Toast */}
      {toast && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-50 bg-gray-900 text-white text-sm font-medium px-5 py-3 rounded-full shadow-lg transition-all">
          {toast}
        </div>
      )}

      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-14 h-14 bg-gray-900 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <i className="ri-settings-3-line text-white text-2xl"></i>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Site Override Controls</h1>
          <p className="text-sm text-gray-500 mt-1">Manually flip site-wide switches — no login required</p>
        </div>

        {/* Cards */}
        <div className="space-y-4">
          {/* Maintenance Mode */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${maintenanceOn ? 'bg-amber-100' : 'bg-gray-100'}`}>
                  <i className={`ri-tools-line text-lg ${maintenanceOn ? 'text-amber-600' : 'text-gray-400'}`}></i>
                </div>
                <div>
                  <p className="font-semibold text-gray-900 text-sm">Maintenance Mode</p>
                  <p className="text-xs text-gray-500 mt-0.5">Shows "We'll be back soon" to all visitors</p>
                </div>
              </div>
              <button
                onClick={handleToggleMaintenance}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${
                  maintenanceOn ? 'bg-amber-500' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ${
                    maintenanceOn ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
            <div className={`mt-3 flex items-center gap-2 text-xs font-medium ${maintenanceOn ? 'text-amber-600' : 'text-gray-400'}`}>
              <div className={`w-2 h-2 rounded-full ${maintenanceOn ? 'bg-amber-500 animate-pulse' : 'bg-gray-300'}`}></div>
              {maintenanceOn ? 'ACTIVE — site is in maintenance mode' : 'OFF — site is accessible normally'}
            </div>
          </div>

          {/* Upgrade Popup */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${upgradeOn ? 'bg-orange-100' : 'bg-gray-100'}`}>
                  <i className={`ri-notification-3-line text-lg ${upgradeOn ? 'text-orange-600' : 'text-gray-400'}`}></i>
                </div>
                <div>
                  <p className="font-semibold text-gray-900 text-sm">Upgrade In Progress Popup</p>
                  <p className="text-xs text-gray-500 mt-0.5">Shows the "upgrade in progress" banner to users</p>
                </div>
              </div>
              <button
                onClick={handleToggleUpgrade}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${
                  upgradeOn ? 'bg-orange-500' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ${
                    upgradeOn ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
            <div className={`mt-3 flex items-center gap-2 text-xs font-medium ${upgradeOn ? 'text-orange-600' : 'text-gray-400'}`}>
              <div className={`w-2 h-2 rounded-full ${upgradeOn ? 'bg-orange-500 animate-pulse' : 'bg-gray-300'}`}></div>
              {upgradeOn ? 'ACTIVE — upgrade popup is showing' : 'OFF — popup is hidden'}
            </div>
          </div>
        </div>

        {/* Kill Switch */}
        <button
          onClick={handleDisableAll}
          className="mt-6 w-full bg-gray-900 hover:bg-gray-800 text-white font-semibold text-sm py-3 rounded-xl transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
        >
          <i className="ri-shut-down-line text-base"></i>
          Disable Everything — Set Site Live
        </button>

        <p className="text-center text-xs text-gray-400 mt-4">
          Changes apply immediately via localStorage. Refresh any other tab to see the effect.
        </p>
      </div>
    </div>
  );
}
